/**
 * ReminderForm - Create/Edit reminder for Device PWA
 * 
 * Features:
 * - Create new reminder
 * - Edit existing reminder
 * - Validate inputs
 * - Large, senior-friendly UI
 */
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Clock, Repeat, Loader } from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

interface Reminder {
  id?: string;
  title: string;
  scheduled_time: string;
  repeat: 'none' | 'daily' | 'weekly' | 'monthly';
}

interface ReminderFormProps {
  mode: 'create' | 'edit';
  reminderId?: string;
  senior: {
    id: string;
    name: string;
  };
  onBack?: () => void;
  onSuccess?: () => void;
}

export default function ReminderForm({ mode, reminderId, senior, onBack, onSuccess }: ReminderFormProps) {
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(mode === 'edit');
  const [title, setTitle] = useState('');
  const [time, setTime] = useState('');
  const [date, setDate] = useState('');
  const [repeat, setRepeat] = useState<'none' | 'daily' | 'weekly' | 'monthly'>('none');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (mode === 'edit' && reminderId) {
      fetchReminder();
    } else {
      // Set default date to today
      const today = new Date();
      setDate(today.toISOString().split('T')[0]);
    }
  }, [mode, reminderId]);

  const fetchReminder = async () => {
    try {
      setFetchingData(true);
      const response = await fetch(`${API}/api/reminders/${reminderId}`);
      
      if (!response.ok) {
        throw new Error('Erinnerung nicht gefunden');
      }
      
      const data = await response.json();
      
      // Parse scheduled_time
      const scheduledDate = new Date(data.scheduled_time);
      setDate(scheduledDate.toISOString().split('T')[0]);
      setTime(scheduledDate.toTimeString().slice(0, 5)); // HH:mm
      setTitle(data.title);
      setRepeat(data.repeat || 'none');
      
      console.log('[ReminderForm] Loaded reminder:', data);
    } catch (err) {
      console.error('[ReminderForm] Fetch error:', err);
      toast.error('Erinnerung konnte nicht geladen werden');
      if (onBack) onBack();
    } finally {
      setFetchingData(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Bitte geben Sie einen Titel ein';
    }

    if (!time) {
      newErrors.time = 'Bitte wählen Sie eine Uhrzeit';
    }

    if (!date) {
      newErrors.date = 'Bitte wählen Sie ein Datum';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      // Build ISO datetime string
      const scheduled_time = `${date}T${time}:00`;

      const payload = {
        user_id: senior.id,
        title: title.trim(),
        scheduled_time,
        repeat,
        status: 'pending',
        reminder_type: 'general'
      };

      let response;
      if (mode === 'create') {
        response = await fetch(`${API}/api/reminders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API}/api/reminders/${reminderId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        throw new Error('Fehler beim Speichern');
      }

      toast.success(mode === 'create' ? 'Erinnerung erstellt' : 'Erinnerung aktualisiert');
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('[ReminderForm] Submit error:', err);
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="w-20 h-20 mx-auto mb-6 animate-spin text-green-600" />
          <p className="text-3xl text-gray-600 font-semibold">Wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-blue-50 p-6 pb-32">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={onBack}
          className="mb-6 flex items-center gap-3 text-gray-600 hover:text-gray-900 text-2xl"
        >
          <ArrowLeft className="w-8 h-8" />
          Zurück
        </button>
        <h1 className="text-5xl font-bold text-gray-900 mb-4">
          {mode === 'create' ? 'Neue Erinnerung' : 'Erinnerung bearbeiten'}
        </h1>
        <p className="text-2xl text-gray-600">für {senior.name}</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Title */}
        <div>
          <label className="block text-2xl font-semibold text-gray-700 mb-3">
            Titel *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z.B. Medikamente einnehmen"
            className="w-full px-6 py-5 text-2xl border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
          />
          {errors.title && (
            <p className="mt-2 text-xl text-red-600">{errors.title}</p>
          )}
        </div>

        {/* Date */}
        <div>
          <label className="block text-2xl font-semibold text-gray-700 mb-3">
            Datum *
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-6 py-5 text-2xl border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
          />
          {errors.date && (
            <p className="mt-2 text-xl text-red-600">{errors.date}</p>
          )}
        </div>

        {/* Time */}
        <div>
          <label className="block text-2xl font-semibold text-gray-700 mb-3 flex items-center gap-3">
            <Clock className="w-7 h-7 text-green-600" />
            Uhrzeit *
          </label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full px-6 py-5 text-2xl border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
          />
          {errors.time && (
            <p className="mt-2 text-xl text-red-600">{errors.time}</p>
          )}
        </div>

        {/* Repeat */}
        <div>
          <label className="block text-2xl font-semibold text-gray-700 mb-3 flex items-center gap-3">
            <Repeat className="w-7 h-7 text-green-600" />
            Wiederholung
          </label>
          <select
            value={repeat}
            onChange={(e) => setRepeat(e.target.value as any)}
            className="w-full px-6 py-5 text-2xl border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all bg-white"
          >
            <option value="none">Einmalig</option>
            <option value="daily">Täglich</option>
            <option value="weekly">Wöchentlich</option>
            <option value="monthly">Monatlich</option>
          </select>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full px-8 py-6 bg-green-500 hover:bg-green-600 text-white rounded-2xl text-2xl font-bold shadow-lg flex items-center justify-center gap-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader className="w-7 h-7 animate-spin" />
              Wird gespeichert...
            </>
          ) : (
            <>
              <Save className="w-7 h-7" />
              {mode === 'create' ? 'Erinnerung erstellen' : 'Änderungen speichern'}
            </>
          )}
        </button>
      </form>
    </div>
  );
}
